import { trackEventServerSide } from "helpers/api";
import { getMajorVersion } from "helpers/services/mixpanel";
import {
  MixpanelActionSource,
  MixpanelEvent,
  PaywallClickedEvent,
  PaywallDismissedEvent,
  PaywallViewedEvent,
} from "helpers/services/mixpanel/events";
import { useModalStack } from "hooks/useModalStack";
import useProToggle from "hooks/useProToggle";
import { useAppDispatch, useAppSelector } from "hooks/useStore";
import { useCallback } from "react";
import {
  CreditedFeature,
  PremiumFeatureSecondaryButtonState,
  setActionSource,
  setDisableProToggleOnPremiumModalClose,
  setPremiumFeatureSecondaryButton,
  setPremiumModalFeature,
} from "store/slices/subscription";

export const PREMIUM_FEATURE_MODAL_ID = "premium-feature-modal";

export const usePremiumFeatureModal = () => {
  const dispatch = useAppDispatch();
  const { openModal, isModalOpen, closeModal, closeAllModals } = useModalStack({
    id: PREMIUM_FEATURE_MODAL_ID,
    blocksScroll: true,
  });
  const { changeProToggle } = useProToggle();
  /**
   * The feature that is currently being displayed in the modal if it is open
   */
  const currentFeature = useAppSelector(
    (state) => state.subscription.upgradeToPremiumModalFeature
  );

  const disableProToggleOnPremiumModalClose = useAppSelector(
    (state) => state.subscription.disableProToggleOnPremiumModalClose
  );

  const secondaryButton = useAppSelector(
    (state) => state.subscription.premiumFeatureSecondaryButton
  );

  const actionSource = useAppSelector(
    (state) => state.subscription.actionSource
  );

  /**
   * @description Tracks an event when the paywall is viewed. Not to be exported.
   * @param actionSource - The action source of the paywall viewed event. Use provided action source instead of redux to ensure
   */
  const trackPaywallViewed = (actionSource: MixpanelActionSource) => {
    const viewedEvent: PaywallViewedEvent = {
      event: MixpanelEvent.PaywallViewed,
      actionSource,
      version: getMajorVersion(),
    };

    trackEventServerSide(viewedEvent);
  };

  /**
   * @description Tracks an event when the paywall is dismissed. Not to be exported.
   */
  const trackPaywallDismissed = useCallback(() => {
    const dismissedEvent: PaywallDismissedEvent = {
      event: MixpanelEvent.PaywallDismissed,
      actionSource: actionSource ?? MixpanelActionSource.PremiumFeatureModal,
      version: getMajorVersion(),
    };

    trackEventServerSide(dismissedEvent);
  }, [actionSource]);

  /**
   * @description Tracks an event when a button is clicked in the paywall.
   * @param button - The button that was clicked
   */
  const trackPaywallButtonClicked = useCallback(
    (button: string) => {
      const clickedEvent: PaywallClickedEvent = {
        event: MixpanelEvent.PaywallClicked,
        actionSource: actionSource ?? MixpanelActionSource.PremiumFeatureModal,
        value: button,
        version: getMajorVersion(),
      };

      trackEventServerSide(clickedEvent);
    },
    [actionSource]
  );

  /**
   * @description Callback to be called when the premium feature modal is closed.
   *              Toggles the pro toggle off if it is on and the disableProToggleOnPremiumModalClose is true.
   *              Resets the disableProToggleOnPremiumModalClose to false on every close.
   *              Resets the premium feature secondary button to null on every close.
   *              Resets the action source to null on every close.
   */
  const onClosePremiumFeatureModal = useCallback(
    (trackPaywallDismissedEvent: boolean = false) => {
      if (trackPaywallDismissedEvent) {
        trackPaywallDismissed();
      }
      if (disableProToggleOnPremiumModalClose) {
        changeProToggle(false);
      }
      dispatch(setDisableProToggleOnPremiumModalClose(false));
      dispatch(setPremiumFeatureSecondaryButton(null));
      dispatch(setActionSource(null));
    },
    [
      changeProToggle,
      disableProToggleOnPremiumModalClose,
      dispatch,
      trackPaywallDismissed,
    ]
  );

  /**
   * @description Opens the premium feature modal
   * @param feature - The feature to display in the modal
   */
  const openPremiumFeatureModal = useCallback(
    ({
      feature,
      actionSource,
      disableProToggle,
      secondaryButton,
    }: {
      feature: CreditedFeature;
      actionSource: MixpanelActionSource;
      disableProToggle?: boolean;
      secondaryButton?: PremiumFeatureSecondaryButtonState;
    }) => {
      dispatch(setPremiumModalFeature(feature));
      dispatch(setActionSource(actionSource));
      dispatch(setPremiumFeatureSecondaryButton(secondaryButton || null));
      if (disableProToggle) {
        dispatch(setDisableProToggleOnPremiumModalClose(disableProToggle));
      }
      trackPaywallViewed(actionSource);
      openModal();
    },
    [dispatch, openModal]
  );

  /**
   * @description Closes the premium feature modal regardless of its position in the modal stack
   */
  const forceClosePremiumFeatureModal = useCallback(
    (onCloseCallback?: () => void) => {
      const closeCallback = () => {
        onClosePremiumFeatureModal(true);
        if (typeof onCloseCallback === "function") {
          onCloseCallback();
        }
      };
      closeModal(closeCallback);
    },
    [closeModal, onClosePremiumFeatureModal]
  );

  return {
    currentFeature,
    openPremiumFeatureModal,
    forceClosePremiumFeatureModal,
    closeAllModals,
    onClosePremiumFeatureModal,
    isPremiumFeatureModalOpen: isModalOpen,
    secondaryButton,
    trackPaywallButtonClicked,
  };
};
